import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { X, FunnelSimple } from '@phosphor-icons/react';
import ListSearch, { filterBySearch, USER_SEARCH_KEYS } from './ListSearch';
import UserAssets from './UserAssets';
import { formatUtcToIst, formatUtcToIstDate } from '../modules/tickets/utils/datetime';
import { ModuleShell, SkeletonBlock, FilterChips } from './module-ui';

import { BACKEND_URL, apiPath } from '../config/apiBase';
const API = apiPath('/api');

const PLATFORM_ASSIGNABLE_ROLES = [
  { value: 'pending', label: 'Pending Approval' },
  { value: 'client', label: 'Client' },
  { value: 'agent', label: 'Agent (field)' },
  { value: 'member', label: 'Member (staff)' },
  { value: 'accounts', label: 'Accounts (payroll / client economics)' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
];

const ALL_ROLE_LABELS = {
  pending: 'Pending Approval',
  client: 'Client',
  end_user: 'Client',
  agent: 'Agent (field)',
  member: 'Member',
  accounts: 'Accounts',
  lead: 'Lead (via Product Team)',
  manager: 'Manager (via Product Team)',
  admin: 'Admin',
  super_admin: 'Super Admin',
};

function displayRole(role) {
  if (role === 'end_user') return 'client';
  return role || 'pending';
}

function roleLabel(role) {
  const key = displayRole(role);
  return ALL_ROLE_LABELS[key] || key.replace('_', ' ');
}

function isOrgManagedRole(role) {
  return ['manager', 'lead'].includes(displayRole(role));
}

function isRequestorRole(role) {
  return ['client', 'agent'].includes(displayRole(role));
}

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    withCredentials: true,
    headers: { Authorization: `Bearer ${token}` },
  };
}

function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [draftRole, setDraftRole] = useState('');
  const [savingRole, setSavingRole] = useState(false);
  const [roleError, setRoleError] = useState(null);
  const [roleSuccess, setRoleSuccess] = useState(null);
  const [filterRole, setFilterRole] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [draftProductIds, setDraftProductIds] = useState([]);
  const [savedProductIds, setSavedProductIds] = useState([]);
  const [draftScopes, setDraftScopes] = useState({});
  const [savedScopes, setSavedScopes] = useState({});
  const [catalogs, setCatalogs] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [users, filterRole, searchQuery]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, authHeaders());
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...users];
    if (filterRole !== 'all') {
      result = result.filter((u) => displayRole(u.role) === filterRole || u.role === filterRole);
    }
    result = filterBySearch(result, searchQuery, USER_SEARCH_KEYS);
    setFilteredUsers(result);
  };

  const scopesFromAccess = (rows) => {
    const out = {};
    (rows || []).forEach((s) => {
      if (!s?.product_id) return;
      out[s.product_id] = {
        client_names: Array.isArray(s.client_names) ? s.client_names : [],
        branches_by_client: s.branches_by_client && typeof s.branches_by_client === 'object'
          ? s.branches_by_client
          : {},
      };
    });
    return out;
  };

  const scopesPayload = (productIds, scopesByPid) => (
    productIds.map((id) => ({
      product_id: id,
      client_names: scopesByPid[id]?.client_names || [],
      branches_by_client: scopesByPid[id]?.branches_by_client || {},
    }))
  );

  const scopesFingerprint = (productIds, scopesByPid) => JSON.stringify(
    productIds.slice().sort().map((id) => ({
      product_id: id,
      client_names: (scopesByPid[id]?.client_names || []).slice().sort(),
      branches_by_client: Object.fromEntries(
        Object.entries(scopesByPid[id]?.branches_by_client || {})
          .map(([k, v]) => [k, (v || []).slice().sort()]),
      ),
    })),
  );

  const loadCatalog = async (productId) => {
    if (!productId) return;
    setCatalogs((prev) => {
      if (prev[productId]?.clients || prev[productId]?.loading) return prev;
      return { ...prev, [productId]: { loading: true, clients: [] } };
    });
    try {
      const res = await axios.get(`${API}/users/raise-catalog/${productId}`, authHeaders());
      setCatalogs((prev) => ({
        ...prev,
        [productId]: { loading: false, clients: res.data?.clients || [] },
      }));
    } catch (error) {
      console.error('Failed to load raise catalog:', error);
      setCatalogs((prev) => ({ ...prev, [productId]: { loading: false, clients: [] } }));
    }
  };

  const loadProductAccess = async (userId) => {
    setLoadingProducts(true);
    try {
      const [productsRes, accessRes] = await Promise.all([
        axios.get(`${API}/products/active`, authHeaders()),
        axios.get(`${API}/users/${userId}/product-access`, authHeaders()),
      ]);
      setAllProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      const ids = accessRes.data?.product_ids || [];
      const scopes = scopesFromAccess(accessRes.data?.scopes || []);
      setDraftProductIds(ids);
      setSavedProductIds(ids);
      setDraftScopes(scopes);
      setSavedScopes(scopes);
      ids.forEach((id) => { loadCatalog(id); });
    } catch (error) {
      console.error('Failed to load product access:', error);
      setAllProducts([]);
      setDraftProductIds([]);
      setSavedProductIds([]);
      setDraftScopes({});
      setSavedScopes({});
    } finally {
      setLoadingProducts(false);
    }
  };

  const openUserModal = (user) => {
    setSelectedUser(user);
    const role = displayRole(user.role);
    const canAssign = PLATFORM_ASSIGNABLE_ROLES.some((r) => r.value === role);
    const nextRole = canAssign ? role : 'member';
    setDraftRole(nextRole);
    setRoleError(null);
    setRoleSuccess(null);
    setDraftProductIds([]);
    setSavedProductIds([]);
    setDraftScopes({});
    setSavedScopes({});
    setShowViewModal(true);
    if (isRequestorRole(nextRole) || isRequestorRole(role)) {
      loadProductAccess(user.user_id);
    }
  };

  const closeUserModal = () => {
    setShowViewModal(false);
    setSelectedUser(null);
    setDraftRole('');
    setRoleError(null);
    setRoleSuccess(null);
    setDraftProductIds([]);
    setSavedProductIds([]);
    setDraftScopes({});
    setSavedScopes({});
    setAllProducts([]);
    setCatalogs({});
  };

  const toggleProduct = (productId) => {
    const removing = draftProductIds.includes(productId);
    setDraftProductIds((prev) => (
      removing ? prev.filter((id) => id !== productId) : [...prev, productId]
    ));
    if (!removing) loadCatalog(productId);
    setDraftScopes((prev) => {
      if (removing) {
        const copy = { ...prev };
        delete copy[productId];
        return copy;
      }
      return prev[productId]
        ? prev
        : { ...prev, [productId]: { client_names: [], branches_by_client: {} } };
    });
    setRoleError(null);
    setRoleSuccess(null);
  };

  const toggleScopeClient = (productId, clientName) => {
    setDraftScopes((prev) => {
      const current = prev[productId] || { client_names: [], branches_by_client: {} };
      const clientNames = current.client_names || [];
      const selected = clientNames.includes(clientName)
        ? clientNames.filter((n) => n !== clientName)
        : [...clientNames, clientName];
      const branches = { ...(current.branches_by_client || {}) };
      if (!selected.includes(clientName)) delete branches[clientName];
      return {
        ...prev,
        [productId]: { client_names: selected, branches_by_client: branches },
      };
    });
    setRoleError(null);
    setRoleSuccess(null);
  };

  const toggleScopeBranch = (productId, clientName, branchName) => {
    setDraftScopes((prev) => {
      const current = prev[productId] || { client_names: [], branches_by_client: {} };
      const clientNames = (current.client_names || []).includes(clientName)
        ? (current.client_names || [])
        : [...(current.client_names || []), clientName];
      const existing = current.branches_by_client?.[clientName] || [];
      const nextBranches = existing.includes(branchName)
        ? existing.filter((b) => b !== branchName)
        : [...existing, branchName];
      return {
        ...prev,
        [productId]: {
          client_names: clientNames,
          branches_by_client: {
            ...(current.branches_by_client || {}),
            [clientName]: nextBranches,
          },
        },
      };
    });
    setRoleError(null);
    setRoleSuccess(null);
  };

  const handleSaveRole = async () => {
    if (!selectedUser || !draftRole) return;
    setSavingRole(true);
    setRoleError(null);
    setRoleSuccess(null);
    try {
      const roleChanged = draftRole !== displayRole(selectedUser.role);
      const productsChanged = isRequestorRole(draftRole) && (
        scopesFingerprint(draftProductIds, draftScopes)
        !== scopesFingerprint(savedProductIds, savedScopes)
      );

      if (roleChanged) {
        const body = { role: draftRole };
        if (isRequestorRole(draftRole)) {
          body.product_ids = draftProductIds;
          body.scopes = scopesPayload(draftProductIds, draftScopes);
        }
        await axios.patch(
          `${API}/users/${selectedUser.user_id}/role`,
          body,
          authHeaders(),
        );
      } else if (productsChanged) {
        await axios.put(
          `${API}/users/${selectedUser.user_id}/product-access`,
          {
            product_ids: draftProductIds,
            scopes: scopesPayload(draftProductIds, draftScopes),
          },
          authHeaders(),
        );
      } else {
        setSavingRole(false);
        return;
      }

      setRoleSuccess(
        isRequestorRole(draftRole)
          ? 'Role and product / client access updated'
          : 'Role updated successfully',
      );
      await fetchUsers();
      setSelectedUser({ ...selectedUser, role: draftRole });
      setSavedProductIds([...draftProductIds]);
      setSavedScopes(JSON.parse(JSON.stringify(draftScopes)));
    } catch (error) {
      const detail = error.response?.data?.detail;
      setRoleError(typeof detail === 'string' ? detail : 'Failed to update user');
    } finally {
      setSavingRole(false);
    }
  };

  const canEditRoles = ['super_admin', 'admin'].includes(currentUser?.role);
  const orgManaged = selectedUser && isOrgManagedRole(selectedUser.role);
  const roleChanged = selectedUser && !orgManaged && draftRole !== displayRole(selectedUser.role);
  const productsChanged = selectedUser && !orgManaged && isRequestorRole(draftRole) && (
    scopesFingerprint(draftProductIds, draftScopes)
    !== scopesFingerprint(savedProductIds, savedScopes)
  );
  const showProductAccess = canEditRoles && !orgManaged && isRequestorRole(draftRole);
  const canSave = Boolean(
    (roleChanged || productsChanged)
    && !(showProductAccess && loadingProducts),
  );

  const filterChips = [];
  if (filterRole !== 'all') {
    filterChips.push({
      id: 'role',
      label: `Role: ${roleLabel(filterRole)}`,
      onRemove: () => setFilterRole('all'),
    });
  }

  return (
    <ModuleShell title="Users" subtitle="Manage users and roles">
    <div data-testid="users-page">

      <div style={{ background: 'white', border: '1px solid #E5E7EB', padding: '10px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <FunnelSimple size={20} weight="bold" />
          <span style={{ fontSize: '12px', fontWeight: '600' }}>Filters</span>
        </div>
        <div style={{ maxWidth: '300px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Role</label>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="input-field"
            style={{ padding: '6px 8px', fontSize: '12px' }}
          >
            <option value="all">All Roles</option>
            {Object.entries(ALL_ROLE_LABELS).filter(([k]) => k !== 'end_user').map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      <FilterChips chips={filterChips} onClearAll={() => setFilterRole('all')} />

      <ListSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search users by name, email, role…"
        resultCount={filteredUsers.length}
        totalCount={users.length}
      />

      {loading ? (
        <SkeletonBlock />
      ) : (
        <div>
          <div style={{ marginBottom: '6px', fontSize: '12px', color: '#6B7280' }}>
            Showing {filteredUsers.length} of {users.length} users
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Designation</th>
                  <th>Role</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.user_id} data-testid="user-row">
                    <td style={{ fontWeight: '500' }}>
                      <button
                        onClick={() => openUserModal(user)}
                        style={{ background: 'none', border: 'none', color: '#002FA7', textDecoration: 'underline', cursor: 'pointer', fontWeight: 'inherit' }}
                        data-testid="user-name-link"
                      >
                        {user.name}
                      </button>
                    </td>
                    <td>{user.email}</td>
                    <td>{user.designation || '—'}</td>
                    <td>
                      <span className="badge">{roleLabel(user.role)}</span>
                    </td>
                    <td>{formatUtcToIstDate(user.created_at)}</td>
                    <td>
                      <button
                        onClick={() => openUserModal(user)}
                        className="btn-secondary"
                        style={{ padding: '4px 12px', fontSize: '12px' }}
                        data-testid="view-user-button"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showViewModal && selectedUser && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          data-testid="view-user-modal"
        >
          <div style={{ background: 'white', padding: '32px', width: '100%', maxWidth: '600px', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ fontSize: '15px', fontWeight: '600' }}>User Details</h2>
              <button onClick={closeUserModal} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            {displayRole(selectedUser.role) === 'pending' && (
              <div style={{ padding: '6px 10px', background: '#FEF3C7', borderRadius: '8px', marginBottom: '8px', fontSize: '12px', color: '#92400E' }}>
                This account is awaiting role assignment. Select a role below and click Save.
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', paddingBottom: '24px', borderBottom: '1px solid #E5E7EB' }}>
              {selectedUser.picture ? (
                <img src={selectedUser.picture} alt={selectedUser.name} style={{ width: '64px', height: '64px', borderRadius: '50%' }} />
              ) : (
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: '#002FA7',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '15px',
                  fontWeight: '600',
                }}
                >
                  {selectedUser.name.charAt(0)}
                </div>
              )}
              <div>
                <p style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>{selectedUser.name}</p>
                <p style={{ fontSize: '12px', color: '#6B7280' }}>{selectedUser.email}</p>
              </div>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>User ID</p>
              <p style={{ fontSize: '12px', fontFamily: 'IBM Plex Mono, monospace' }}>{selectedUser.user_id}</p>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Current Role</p>
              <p style={{ fontSize: '12px', marginBottom: '6px' }}>
                <span className="badge">{roleLabel(selectedUser.role)}</span>
              </p>

              {canEditRoles && orgManaged && (
                <p style={{ fontSize: '12px', color: '#6B7280', padding: '12px', background: '#F9FAFB', borderRadius: '8px' }}>
                  This user&apos;s {displayRole(selectedUser.role)} role is set via Product Management.
                  Change or remove them there to update team leadership roles.
                </p>
              )}

              {canEditRoles && !orgManaged && (
                <>
                  <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Assign platform role</p>
                  <p style={{ fontSize: '12px', color: '#92400E', marginBottom: '8px' }}>
                    Manager and Lead are not assigned here — use Product Management to set team roles.
                  </p>
                  <select
                    value={draftRole}
                    onChange={(e) => {
                      const next = e.target.value;
                      setDraftRole(next);
                      setRoleError(null);
                      setRoleSuccess(null);
                      if (isRequestorRole(next) && selectedUser) {
                        if (!allProducts.length) {
                          loadProductAccess(selectedUser.user_id);
                        }
                      }
                    }}
                    className="input-field"
                    style={{ maxWidth: '280px', width: '100%' }}
                    data-testid="role-select"
                  >
                    {PLATFORM_ASSIGNABLE_ROLES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>

                  {showProductAccess && (
                    <div
                      style={{
                        marginTop: '12px',
                        padding: '12px',
                        background: '#F9FAFB',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB',
                      }}
                      data-testid="product-access-checklist"
                    >
                      <p style={{ fontSize: '12px', fontWeight: 600, margin: '0 0 4px' }}>
                        Products they can raise tickets for
                      </p>
                      <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 10px' }}>
                        Product only = they pick any company/branch on that product.
                        Optionally restrict to specific companies and branches so the ticket form fills itself.
                      </p>
                      {loadingProducts ? (
                        <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>Loading products…</p>
                      ) : allProducts.length === 0 ? (
                        <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>
                          No active products yet. Create products in Product Management first.
                        </p>
                      ) : (
                        <div style={{ display: 'grid', gap: '8px', maxHeight: '360px', overflowY: 'auto' }}>
                          {allProducts.map((p) => {
                            const checked = draftProductIds.includes(p.product_id);
                            const scope = draftScopes[p.product_id] || { client_names: [], branches_by_client: {} };
                            const catalog = catalogs[p.product_id] || { loading: false, clients: [] };
                            return (
                              <div
                                key={p.product_id}
                                style={{
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '8px',
                                  padding: '8px',
                                  background: checked ? '#fff' : '#F9FAFB',
                                }}
                              >
                                <label
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleProduct(p.product_id)}
                                  />
                                  <span>{p.name}</span>
                                  {p.accept_tickets === false && (
                                    <span style={{ color: '#B45309', fontSize: '11px', fontWeight: 400 }}>(not accepting)</span>
                                  )}
                                </label>
                                {checked && (
                                  <ProductRaiseScope
                                    catalog={catalog}
                                    scope={scope}
                                    onToggleClient={(name) => toggleScopeClient(p.product_id, name)}
                                    onToggleBranch={(clientName, branch) => toggleScopeBranch(p.product_id, clientName, branch)}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {roleError && (
                    <p style={{ color: '#B91C1C', fontSize: '12px', marginTop: '8px' }}>{roleError}</p>
                  )}
                  {roleSuccess && (
                    <p style={{ color: '#047857', fontSize: '12px', marginTop: '8px' }}>{roleSuccess}</p>
                  )}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    <button
                      type="button"
                      onClick={handleSaveRole}
                      disabled={savingRole || !canSave}
                      className="btn-primary"
                      style={{ padding: '5px 10px', opacity: savingRole || !canSave ? 0.6 : 1 }}
                      data-testid="save-role-button"
                    >
                      {savingRole ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={closeUserModal}
                      className="btn-secondary"
                      style={{ padding: '5px 10px' }}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}

              {canEditRoles && orgManaged && (
                <div style={{ marginTop: '12px' }}>
                  <button
                    type="button"
                    onClick={closeUserModal}
                    className="btn-secondary"
                    style={{ padding: '5px 10px' }}
                  >
                    Close
                  </button>
                </div>
              )}
              {!canEditRoles && (
                <span className="badge">{roleLabel(selectedUser.role)}</span>
              )}
            </div>

            <div style={{ marginBottom: '8px' }}>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Designation</p>
              <p style={{ fontSize: '12px' }}>{selectedUser.designation || '—'}</p>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Created At</p>
              <p style={{ fontSize: '12px' }}>{formatUtcToIst(selectedUser.created_at)}</p>
            </div>

            <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '16px', marginTop: '8px' }}>
              <UserAssets userId={selectedUser.user_id} />
            </div>
          </div>
        </div>
      )}
    </div>
    </ModuleShell>
  );
}

function ProductRaiseScope({ catalog, scope, onToggleClient, onToggleBranch }) {
  const [q, setQ] = useState('');
  const clients = catalog?.clients || [];
  const query = q.trim().toLowerCase();
  const filtered = !query
    ? clients
    : clients.filter((c) => (
      (c.name || '').toLowerCase().includes(query)
      || (c.label || '').toLowerCase().includes(query)
    ));
  const selectedNames = scope?.client_names || [];
  if (catalog?.loading) {
    return <p style={{ fontSize: '11px', color: '#6B7280', margin: '8px 0 0' }}>Loading companies…</p>;
  }
  if (!clients.length) {
    return (
      <p style={{ fontSize: '11px', color: '#6B7280', margin: '8px 0 0' }}>
        No company catalog on this product — product-only access. They can pick any company on the form.
      </p>
    );
  }
  return (
    <div style={{ marginTop: '8px', paddingLeft: '4px' }}>
      <p style={{ fontSize: '11px', color: '#6B7280', margin: '0 0 6px' }}>
        Companies (optional). Leave none selected to allow all.
      </p>
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${clients.length} companies…`}
        style={{
          width: '100%',
          padding: '5px 8px',
          borderRadius: '8px',
          border: '1px solid #D1D5DB',
          marginBottom: '6px',
          fontSize: '12px',
        }}
      />
      <div style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 8px', background: '#fff' }}>
        {filtered.length ? filtered.map((c) => {
          const checked = selectedNames.includes(c.name);
          const branches = c.branches || [];
          const selectedBranches = scope?.branches_by_client?.[c.name] || [];
          return (
            <div key={c.name} style={{ marginBottom: branches.length && checked ? 6 : 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, minHeight: 26, cursor: 'pointer' }}>
                <input type="checkbox" checked={checked} onChange={() => onToggleClient(c.name)} />
                {c.label || c.name}{c.parent_value ? ` (${c.parent_value})` : ''}
              </label>
              {checked && branches.length > 0 && (
                <div style={{ margin: '0 0 4px 22px' }}>
                  <p style={{ fontSize: '11px', color: '#6B7280', margin: '0 0 4px' }}>
                    Branches (optional). Leave none selected to allow all.
                  </p>
                  {branches.map((b) => (
                    <label key={b} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, minHeight: 24, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedBranches.includes(b)}
                        onChange={() => onToggleBranch(c.name, b)}
                      />
                      {b}
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        }) : (
          <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>No companies match “{q}”</p>
        )}
      </div>
    </div>
  );
}

export default UsersPage;
